import { z } from "zod";
import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getOneIngredientKey } from "../ingredients/useGetOneIngredient";
import { getIngredientScoresKey } from "../olapScores/useGetIngredientScores";

const baseUrl = "/api/ingredient_supplier_activity_datas" as const;

type UpsertIngredientSupplierActivityData = {
    ingredientId?: number;
};

export const useUpsertIngredientSupplierActivityData = (options: UpsertIngredientSupplierActivityData) => {
    const queryClient = useQueryClient();
    const { selectedCompany } = useFoodpilotContext();

    return useMutation({
        mutationFn: async (activityData: IngredientSupplierActivityDataWrite) => {
            if (selectedCompany === null) {
                throw new Error("Cannot save activity data without a selected organization");
            }

            const config = { params: { organization_id: selectedCompany.id } };

            return activityData?.id === undefined ?
                    await loggedInApiCall.post(baseUrl, activityData, config)
                :   await loggedInApiCall.put(`${baseUrl}/${activityData.id}`, activityData, config);
        },
        onSuccess: () => {
            const { ingredientId } = options;

            if (ingredientId === undefined) return;

            queryClient.refetchQueries([getOneIngredientKey, ingredientId.toString()]);
            queryClient.refetchQueries([getIngredientScoresKey, ingredientId]);
        },
    });
};

export const IngredientSupplierActivityDataWriteSchema = z.object({
    id: z.number().optional(),
    campaign: z.string(),
    ingredientSupplier: z.string().optional(),
    quantity: z.number().nullable().optional(),
    grossSales: z.number().nullable().optional(),
});

export type IngredientSupplierActivityDataWrite = z.infer<typeof IngredientSupplierActivityDataWriteSchema>;
